import { useQueries } from "@tanstack/react-query";
import { Skeleton } from "antd";
import L from "leaflet";
import { useEffect, useMemo } from "react";
import { MapContainer, Polygon, Popup, TileLayer, useMap } from "react-leaflet";
import { TERRITORY_QUERY_KEY } from "@/modules/territories/hooks/useTerritories";
import type { Territory } from "@/modules/territories/services/territory.service";
import { territoryService } from "@/modules/territories/services/territory.service";
import { EmptyState } from "@/shared/components/EmptyState";
import { geoJsonToLeafletPositions, geometryToLatLngs } from "@/shared/map/geojson";

const POLYGON_COLOR = "#1677ff";

function FitToPolygons({ positions }: { positions: [number, number][] }) {
	const map = useMap();

	useEffect(() => {
		if (positions.length === 0) {
			return;
		}
		map.fitBounds(L.latLngBounds(positions), { padding: [20, 20], maxZoom: 16 });
	}, [map, positions]);

	return null;
}

interface BrigadeTerritoryMapProps {
	territoryIds: string[];
}

interface TerritoryPolygon {
	territory: Territory;
	positions: [number, number][] | [number, number][][][];
	boundsPositions: [number, number][];
}

/** Small read-only map showing all of the brigade's territory polygons */
export function BrigadeTerritoryMap({ territoryIds }: BrigadeTerritoryMapProps) {
	// `combine` is memoized by TanStack Query, so `territories` keeps a stable
	// identity between renders and FitToPolygons doesn't refit on every render.
	const { isLoading, territories } = useQueries({
		queries: territoryIds.map((id) => ({
			queryKey: [TERRITORY_QUERY_KEY, "detail", id],
			queryFn: () => territoryService.getById(id),
		})),
		combine: (results) => ({
			isLoading: results.some((r) => r.isLoading),
			territories: results
				.map((r) => r.data)
				.filter((t): t is Territory => !!t?.coordinates?.coordinates?.length)
				.map(
					(t): TerritoryPolygon => ({
						territory: t,
						positions: t.coordinates ? geoJsonToLeafletPositions(t.coordinates.coordinates) : [],
						boundsPositions: t.coordinates ? geometryToLatLngs(t.coordinates) : [],
					})
				),
		}),
	});

	const allPositions = useMemo(() => territories.flatMap((t) => t.boundsPositions), [territories]);

	if (isLoading) {
		return <Skeleton.Node active style={{ width: "100%", height: 280 }} />;
	}

	if (allPositions.length === 0) {
		return <EmptyState description="Hudud koordinatalari topilmadi" />;
	}

	return (
		<div className="h-[280px] w-full rounded-lg overflow-hidden border border-gray-200">
			<MapContainer
				center={allPositions[0]}
				zoom={13}
				className="h-full w-full"
				scrollWheelZoom={false}
			>
				<TileLayer
					attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
					url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
				/>
				<FitToPolygons positions={allPositions} />
				{territories.map(({ territory, positions }) => (
					<Polygon
						key={territory.id}
						positions={positions as any}
						pathOptions={{
							color: POLYGON_COLOR,
							fillColor: POLYGON_COLOR,
							fillOpacity: 0.3,
							weight: 2,
						}}
					>
						<Popup>
							<div className="text-sm min-w-[160px]">
								<p className="font-semibold mb-1">{territory.name}</p>
								<p className="mb-1">
									Yuzasi: <strong>{territory.area?.toLocaleString("uz-UZ") ?? "—"} m²</strong>
								</p>
								<p className="mb-0">
									Perimetr: <strong>{territory.perimeter?.toLocaleString("uz-UZ") ?? "—"} m</strong>
								</p>
							</div>
						</Popup>
					</Polygon>
				))}
			</MapContainer>
		</div>
	);
}
